/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/naming-convention */
import { Box } from '@mui/material'
import React, { lazy, Suspense, useEffect, useState } from 'react'
import LoadingPage from '../../../pages/LoadingPage'
import { getCourseLoading, getSlotSections } from '../../../store/slices/distributionPlanSlice/selectors'
import { CreateAndEditCourses } from './CreateAndEditCourses'
import { type IDistributionPlanList, type ISlot } from '../../../models/DistributionPlan'
import { type Row, type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'
import { useAppDispatch } from '../../../store'
import { getSlotSectionApi } from '../../../store/slices/distributionPlanSlice/apis'
import { useParams, useSearchParams } from 'react-router-dom'
import CoursesTopbarActions from './CoursesTopbarActions'
import CourseDelete from './CourseDelete'
import { exportCsv } from '../../../utils/exportCsv'
import { notification } from '../../../components/Notifications'
import ROUTES from '../../../consts/paths'
import { type ISectionsList, type ISectionRow } from '../../../models/TermsSection'
import CustomHeader from '../../../components/CustomHeader'
import { useMutation } from '@tanstack/react-query'
import { DistributionManager } from '../../../APIManager'

const AppTable = lazy(async () => await import('../../../components/CustomTable'))
interface CoursesProps {
  slot: ISlot
}
interface GoLiveParams {
  slotId: string
  sectionId: string
}

const Courses: React.FC<CoursesProps> = ({ slot }) => {
  const [searchParams] = useSearchParams()
  const searchValue = searchParams.get('search')
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({ search: searchValue || '' })

  const { id } = useParams()
  const dispatch = useAppDispatch()

  const isCourseLoading = getCourseLoading()
  const slotSections = getSlotSections()

  const isSlotSections = (slotSections?.data?.length ?? 0) > 0

  // TEMPORARY: For now, remove "EDIT" from actions in CDG on the FE.
  // BE team needs to handle this logic and remove "EDIT" from the BE response in the future.

  const slotSectionsWithModifiedActions = {
    ...slotSections,
    data: slotSections?.data?.map((section: ISectionRow) => {
      const { slotStatus, earlyAccessReleaseTimestamp_1, earlyAccessReleaseTimestamp_2 } = section.data || {}

      return {
        ...section,
        data: {
          ...section.data,
          earlyAccessReleaseTimestamp_1: earlyAccessReleaseTimestamp_1 ?? slot?.releaseTimestamp,
          earlyAccessReleaseTimestamp_2: earlyAccessReleaseTimestamp_2 ?? slot?.releaseTimestamp
        },
        actions: slotStatus === 'Closed'
          ? (section.actions || []).filter(action => action !== 'EDIT')
          : section.actions
      }
    })
  }

  useEffect(() => {
    void dispatch(getSlotSectionApi({ urlId: id, params }))
  }, [id, params])

  const { mutate: exportSectionCSVMutate, isPending: exportLoading } = useMutation({
    mutationFn: DistributionManager.exportSectionsCSV,
    onSuccess: (response) => {
      const data = response?.data
      if (data) {
        exportCsv({ data, headers: response?.headers })
      }
    },
    onError: (error: any) => {
      notification(error?.message ?? 'Export csv failed', { variant: 'error' })
    }
  })

  const handleExport = (): void => {
    const exportParams = {
      slotId: slot.slotId,
      search: params?.search || '',
      sort: typeof params?.sort === 'string' ? params.sort : undefined,
      pageSize: params?.pageSize || 10
    }
    exportSectionCSVMutate(exportParams)
  }

  const handleSurveyPreview = (row: IDistributionPlanList | Row): void => {
    const selectedRowData = row.data
    const surveyPreviewUrl = `${ROUTES.SURVEY_PREVIEW}/${selectedRowData?.slotId}/${selectedRowData?.linkedSection?.sectionId}`
    window.open(surveyPreviewUrl, '_blank')
  }

  const { mutate: handleGoLive } = useMutation({
    mutationFn: async (params: GoLiveParams) => {
      return await DistributionManager.goLiveSectionApi(params)
    },
    onSuccess: async (data) => {
      notification(data?.data?.message ?? 'Section successfully live', { variant: 'success' })
      await dispatch(getSlotSectionApi({ urlId: id, params }))
    },
    onError: (error: any) => {
      notification(error?.response?.data?.error ?? 'Internal server error', { variant: 'error' })
    }
  })

  const handleGoLIVE = (row: IDistributionPlanList | Row): void => {
    const slotId = row?.data?.slotId
    const sectionId = row?.data?.linkedSection?.sectionId
    if (slotId && sectionId) {
      const params = { slotId, sectionId }
      handleGoLive(params)
    } else {
      console.error('Error: Invalid row data. Either slotId or sectionId is missing.')
      notification('Invalid row data. Please check the slot ID and section ID.', { variant: 'error' })
    }
  }
  return (
    <Box>
      <Suspense fallback={<LoadingPage />}>
        <CustomHeader info={`${slot.term.termName} | ${slot?.title} `} />
        <AppTable
          exportCsv={handleExport}
          exportButtonProps={{ loading: exportLoading, disabled: exportLoading }}
          loading={isCourseLoading}
          list={slotSectionsWithModifiedActions as ISectionsList}
          noDataComponentProps={{
            title: ' 1. Start by importing your courses',
            subTitle: 'Survey Projects cannot be published until you have uploaded your courses.'
          }}
          customTopbarAction={<CoursesTopbarActions slot={slot} params={params} isSlotSections={isSlotSections} />}
          featureName='Course Section Assignment'
          editAction={<CreateAndEditCourses slot={slot} />}
          deleteAction={<CourseDelete params={params} slot={slot} />}
          params={params}
          setParams={setParams}
          onPreviewClick={handleSurveyPreview}
          onGoLiveClick={handleGoLIVE}
        />
      </Suspense>
    </Box>
  )
}

export default Courses
